import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('全部');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // SEO Configuration
  useSEO({
    title: '平衡計分卡導入服務 | 企業策略管理顧問 | BSC專業諮詢',
    description: '專業平衡計分卡(BSC)顧問服務，協助企業將策略轉化為可衡量的行動方案。提供策略規劃、KPI指標設計、績效管理系統導入等完整服務。超過50家企業成功案例，涵蓋製造業、服務業、科技業。立即預約免費諮詢，實現企業願景與永續成長。',
    keywords: '平衡計分卡,BSC,策略管理,績效管理,KPI指標,企業顧問,策略規劃',
    canonical: `${siteUrl}/`,
    ogTitle: '平衡計分卡導入服務 | 企業策略管理顧問',
    ogDescription: '專業平衡計分卡顧問服務，協助企業實現策略目標與永續成長',
    ogType: 'website',
    schemaId: 'home',
    schemaData: [
      {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: '平衡計分卡顧問公司',
        description: '專業的平衡計分卡導入與策略管理顧問服務',
        url: siteUrl,
        serviceType: ['策略管理諮詢', '平衡計分卡導入', 'KPI指標設計', '績效管理系統'],
        areaServed: {
          '@type': 'Country',
          name: 'Taiwan'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '50'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '什麼是平衡計分卡（BSC）？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '平衡計分卡（Balanced Scorecard, BSC）是一套策略管理工具，由哈佛商學院教授 Kaplan 與 Norton 於1992年提出。它從財務、顧客、內部流程、學習與成長四個構面，全面衡量企業績效，協助企業將願景與策略轉化為具體可執行的行動方案。'
            }
          },
          {
            '@type': 'Question',
            name: '導入平衡計分卡需要多長時間？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '一般而言，完整的平衡計分卡導入專案約需3至6個月。包含需求訪談、策略共識建立、指標設計、系統導入與初步運行。實際時間會依企業規模、組織複雜度與配合程度而有所調整。'
            }
          },
          {
            '@type': 'Question',
            name: '平衡計分卡適合哪些產業？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '平衡計分卡適用於所有產業，包括製造業、服務業、科技業、金融業、醫療業、教育機構及政府單位等。全球超過50%的財富500大企業都採用BSC作為策略管理工具。無論企業規模大小，都能從中受益。'
            }
          },
          {
            '@type': 'Question',
            name: '中小企業也適合導入平衡計分卡嗎？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '非常適合。中小企業資源有限，更需要聚焦策略方向。平衡計分卡能幫助中小企業釐清優先順序，將有限資源投入最關鍵的策略目標，避免資源分散。我們會依據企業規模調整導入方式，確保實用且可執行。'
            }
          },
          {
            '@type': 'Question',
            name: '導入平衡計分卡後，多久能看到成效？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '通常在導入後3至6個月即可看到初步成效，例如策略共識提升、目標更加明確、跨部門協作改善等。財務面的顯著成長通常在6至12個月後逐步顯現。持續運行與優化是長期成功的關鍵。'
            }
          },
          {
            '@type': 'Question',
            name: '平衡計分卡與KPI有什麼不同？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'KPI（關鍵績效指標）是衡量績效的工具，而平衡計分卡是一套完整的策略管理框架。BSC不僅包含KPI，還涵蓋策略地圖、目標值設定、行動方案規劃等。簡單來說，KPI告訴你「衡量什麼」，BSC告訴你「為什麼衡量」以及「如何達成」。'
            }
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${siteUrl}/#webpage`,
        url: `${siteUrl}/`,
        name: '平衡計分卡導入服務 | 企業策略管理顧問 | BSC專業諮詢',
        description: '專業平衡計分卡(BSC)顧問服務，協助企業將策略轉化為可衡量的行動方案。',
        inLanguage: 'zh-TW',
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: '平衡計分卡顧問公司',
          inLanguage: 'zh-TW'
        }
      }
    ]
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const industries = ['全部', '製造業', '服務業', '科技業'];

  const cases = [
    {
      industry: '製造業',
      image: 'https://public.readdy.ai/ai/img_res/bb9ab26ca215b8bf61b753bf6b2caf50.jpg',
      title: '傳統製造業轉型升級',
      company: '精密機械公司',
      results: [
        { metric: '營收成長', value: '35%' },
        { metric: '生產效率提升', value: '42%' }
      ]
    },
    {
      industry: '服務業',
      image: 'https://readdy.ai/api/search-image?query=modern%20retail%20service%20environment%20with%20customer%20interaction%20and%20digital%20technology%20integration%2C%20clean%20professional%20space%20with%20warm%20lighting%2C%20showcasing%20excellent%20customer%20service%20experience%20and%20operational%20efficiency&width=600&height=400&seq=case2&orientation=landscape',
      title: '連鎖零售業績效管理',
      company: '連鎖零售集團',
      results: [
        { metric: '客戶滿意度', value: '28%' },
        { metric: '門市坪效', value: '31%' }
      ]
    },
    {
      industry: '科技業',
      image: 'https://readdy.ai/api/search-image?query=innovative%20technology%20company%20office%20with%20modern%20workspace%20design%2C%20collaborative%20environment%20featuring%20digital%20screens%20and%20creative%20team%20meetings%2C%20bright%20clean%20aesthetic%20emphasizing%20innovation%20and%20productivity&width=600&height=400&seq=case3&orientation=landscape',
      title: '科技新創快速成長',
      company: '軟體科技公司',
      results: [
        { metric: '產品上市時間', value: '-40%' },
        { metric: '員工留任率', value: '25%' }
      ]
    },
    {
      industry: '製造業',
      image: 'https://readdy.ai/api/search-image?query=pharmaceutical%20manufacturing%20clean%20room%20with%20strict%20quality%20control%20and%20advanced%20equipment%2C%20sterile%20professional%20environment%20with%20precise%20monitoring%20systems%2C%20emphasizing%20safety%20and%20compliance%20standards&width=600&height=400&seq=case4&orientation=landscape',
      title: '醫療器材品質提升',
      company: '醫療器材製造商',
      results: [
        { metric: '不良率降低', value: '65%' },
        { metric: '認證通過率', value: '100%' }
      ]
    },
    {
      industry: '服務業',
      image: 'https://readdy.ai/api/search-image?query=modern%20financial%20services%20office%20with%20professional%20consultants%20and%20clients%20in%20meeting%2C%20sophisticated%20business%20environment%20with%20digital%20displays%2C%20clean%20bright%20space%20emphasizing%20trust%20and%20expertise&width=600&height=400&seq=case5&orientation=landscape',
      title: '金融服務數位轉型',
      company: '金融服務公司',
      results: [
        { metric: '數位通路占比', value: '58%' },
        { metric: '服務成本降低', value: '33%' }
      ]
    },
    {
      industry: '科技業',
      image: 'https://readdy.ai/api/search-image?query=telecommunications%20network%20operations%20center%20with%20advanced%20monitoring%20systems%20and%20technical%20staff%2C%20high-tech%20professional%20environment%20with%20multiple%20screens%20displaying%20network%20data%2C%20emphasizing%20reliability%20and%20innovation&width=600&height=400&seq=case6&orientation=landscape',
      title: '電信業客戶體驗優化',
      company: '電信營運商',
      results: [
        { metric: 'NPS分數提升', value: '45分' },
        { metric: '客訴率降低', value: '52%' }
      ]
    }
  ];

  const filteredCases = selectedIndustry === '全部' 
    ? cases 
    : cases.filter(c => c.industry === selectedIndustry);

  const reviews = [
    {
      name: '張執行長',
      company: '精密機械公司 執行長',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20executive%20in%20business%20suit%2C%20confident%20leadership%20portrait%20with%20clean%20background%2C%20mature%20experienced%20business%20leader%20with%20warm%20professional%20demeanor&width=200&height=200&seq=avatar1&orientation=squarish',
      content: '導入平衡計分卡後，我們終於能將策略轉化為具體行動。顧問團隊專業且耐心，協助我們建立了完整的績效管理系統。',
      rating: 5
    },
    {
      name: '李總經理',
      company: '連鎖零售集團 總經理',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20executive%20in%20business%20attire%2C%20confident%20corporate%20leader%20portrait%20with%20neutral%20background%2C%20experienced%20manager%20with%20approachable%20professional%20appearance&width=200&height=200&seq=avatar2&orientation=squarish',
      content: '從策略規劃到執行追蹤，整個過程非常順暢。現在各門市都能清楚了解自己的目標，績效顯著提升。',
      rating: 5
    },
    {
      name: '王董事長',
      company: '軟體科技公司 董事長',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20tech%20entrepreneur%20in%20smart%20casual%20attire%2C%20innovative%20leader%20portrait%20with%20modern%20background%2C%20young%20dynamic%20executive%20with%20confident%20friendly%20expression&width=200&height=200&seq=avatar3&orientation=squarish',
      content: '作為新創公司，我們需要快速成長但又要保持方向正確。平衡計分卡幫助我們在擴張中保持策略聚焦。',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/9fa41e12-cf6f-466e-a521-df80e2d2b6a9.png" alt="平衡計分卡顧問" className="h-12 w-auto" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>首頁</a>
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>關於平衡計分卡</a>
              <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>服務內容</a>
              <Link to="/team" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>顧問團隊</Link>
              <a href="#cases" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>成功案例</a>
              <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>常見問題</a>
              <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>聯絡我們</a>
            </div>
            <div className="flex items-center gap-4">
              <a href="#contact" className="hidden md:block bg-[#D4AF37] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#C19B2B] transition-colors whitespace-nowrap cursor-pointer">
                免費諮詢
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <a 
                href="#home" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                首頁
              </a>
              <a 
                href="#about" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                關於平衡計分卡
              </a>
              <a 
                href="#services" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                服務內容
              </a>
              <Link 
                to="/team" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                顧問團隊
              </Link>
              <a 
                href="#cases" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                成功案例
              </a>
              <a 
                href="#faq" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                常見問題
              </a>
              <a 
                href="#contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                聯絡我們
              </a>
              <a 
                href="#contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#D4AF37] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#C19B2B] transition-colors text-center cursor-pointer"
              >
                免費諮詢
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20professional%20business%20strategy%20visualization%20with%20geometric%20patterns%20and%20flowing%20lines%2C%20modern%20corporate%20background%20in%20deep%20blue%20and%20teal%20gradient%20tones%2C%20sophisticated%20minimalist%20design%20emphasizing%20strategic%20planning%20and%20organizational%20excellence%20with%20subtle%20golden%20accents&width=1920&height=1080&seq=hero1&orientation=landscape" 
            alt="Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A3A52]/85 via-[#2C5F7F]/80 to-[#1A3A52]/85"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="flex items-center justify-between">
            <div className="w-full md:w-[45%]">
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
                實現企業願景<br />
                從策略到執行<br />
                <span className="text-[#D4AF37]">平衡計分卡專業顧問</span>
              </h1>
              <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                協助企業將策略轉化為可衡量的行動方案，透過四大構面的平衡管理，實現永續成長與卓越績效
              </p>
              <div className="flex items-center space-x-4">
                <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>了解更多</a>
                <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>預約諮詢</a>
              </div>
            </div>

            <div className="hidden md:flex w-[45%] items-center justify-center">
              <div className="relative w-96 h-96">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-40 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-3xl text-[#D4AF37]"></i>
                  </div>
                  <div className="text-white font-semibold text-sm">財務構面</div>
                </div>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-40 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-user-heart-line text-3xl text-[#D4AF37]"></i>
                  </div>
                  <div className="text-white font-semibold text-sm">顧客構面</div>
                </div>
                <div className="absolute top-1/2 right-0 -translate-y-1/2 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-40 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-settings-3-line text-3xl text-[#D4AF37]"></i>
                  </div>
                  <div className="text-white font-semibold text-sm">內部流程</div>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-40 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-3xl text-[#D4AF37]"></i>
                  </div>
                  <div className="text-white font-semibold text-sm">學習成長</div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-[#D4AF37] rounded-full"></div>
                <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                  <line x1="50%" y1="20%" x2="50%" y2="50%" stroke="rgba(212,175,55,0.3)" strokeWidth="2" strokeDasharray="5,5" />
                  <line x1="20%" y1="50%" x2="50%" y2="50%" stroke="rgba(212,175,55,0.3)" strokeWidth="2" strokeDasharray="5,5" />
                  <line x1="80%" y1="50%" x2="50%" y2="50%" stroke="rgba(212,175,55,0.3)" strokeWidth="2" strokeDasharray="5,5" />
                  <line x1="50%" y1="80%" x2="50%" y2="50%" stroke="rgba(212,175,55,0.3)" strokeWidth="2" strokeDasharray="5,5" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About BSC Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="w-full md:w-[58%]">
              <div className="inline-block bg-[#D4AF37] text-[#1A3A52] px-4 py-1.5 rounded-full text-xs font-semibold mb-6">
                ABOUT BSC
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-8 leading-tight">
                什麼是<br />
                <span className="text-[#D4AF37]">平衡計分卡</span>
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p className="text-base">
                  平衡計分卡（Balanced Scorecard, BSC）是由哈佛商學院教授 Robert Kaplan 與企業顧問 David Norton 於1992年提出的策略管理工具。它突破了傳統只重視財務指標的管理方式，從財務、顧客、內部流程、學習與成長四個構面，全面衡量企業績效。
                </p>
                <p className="text-base">
                  這套管理工具不僅是績效衡量系統，更是策略執行的框架。它協助企業將願景與策略轉化為具體的目標、衡量指標、目標值與行動方案，讓組織上下都能清楚了解策略方向，並透過因果關係的連結，確保各項行動都能支持整體策略目標的達成。
                </p>
                <p className="text-base">
                  全球超過50%的財富500大企業採用平衡計分卡作為策略管理工具。無論是製造業、服務業或科技業，平衡計分卡都能協助企業在快速變動的環境中，保持策略聚焦，實現永續成長。
                </p>
              </div>
            </div>

            <div className="w-full md:w-[38%]">
              <div className="bg-gradient-to-br from-[#E8F4F8] to-[#D4E8F0] rounded-3xl p-8">
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center">
                        <i className="ri-line-chart-line text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-[#1A3A52]">財務構面</h3>
                    </div>
                    <p className="text-sm text-gray-600">衡量企業的財務績效與股東價值創造</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#4A90E2] rounded-xl flex items-center justify-center">
                        <i className="ri-user-star-line text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-[#1A3A52]">顧客構面</h3>
                    </div>
                    <p className="text-sm text-gray-600">關注顧客滿意度與市場定位策略</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#50C878] rounded-xl flex items-center justify-center">
                        <i className="ri-flow-chart text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-[#1A3A52]">內部流程</h3>
                    </div>
                    <p className="text-sm text-gray-600">優化關鍵營運流程以創造價值</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#9B59B6] rounded-xl flex items-center justify-center">
                        <i className="ri-graduation-cap-line text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-[#1A3A52]">學習成長</h3>
                    </div>
                    <p className="text-sm text-gray-600">培養組織能力與創新文化</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="services" className="py-24 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-[#D4AF37] text-sm font-semibold mb-3">OUR VALUE</div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] leading-tight">
              平衡計分卡如何<br />幫助企業實現願景
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-[#4A90E2] to-[#357ABD] rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-compass-3-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A3A52] mb-4 text-center">策略規劃</h3>
              <p className="text-gray-600 leading-relaxed text-center text-sm">
                協助企業將抽象的願景、策略目標與現況挑戰，轉化為清晰的策略目標。透過策略地圖的繪製，建立各構面目標之間的因果關係，確保所有行動都能支持整體策略方向，讓組織上下對策略有一致的理解與共識。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-dashboard-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A3A52] mb-4 text-center">指標設計</h3>
              <p className="text-gray-600 leading-relaxed text-center text-sm">
                為每個策略目標設計適切的衡量指標（KPI），包含領先指標與落後指標。領先指標預測未來績效，落後指標衡量過去成果。透過平衡的指標組合，企業能夠即時掌握策略執行狀況，及早發現問題並調整方向。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-[#50C878] to-[#3DA760] rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-rocket-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A3A52] mb-4 text-center">執行追蹤</h3>
              <p className="text-gray-600 leading-relaxed text-center text-sm">
                建立定期的績效檢討機制，透過儀表板即時監控各項指標的達成狀況。當指標出現異常時，能夠快速分析原因並採取改善行動。持續的追蹤與回饋循環，確保策略能夠有效執行，並隨環境變化適時調整。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Process Timeline */}
      <section className="py-24 bg-gradient-to-br from-[#1A3A52] via-[#2C5F7F] to-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">我們的服務流程</h2>
            <div className="w-24 h-1 bg-[#D4AF37]/30 mx-auto"></div>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-20 left-0 right-0 h-0.5 bg-[#D4AF37]/30"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {[
                { num: '01', title: '需求訪談', desc: '深入了解企業願景、策略目標與現況挑戰' },
                { num: '02', title: '策略共識', desc: '與管理團隊共同繪製策略地圖，建立共識' },
                { num: '03', title: '指標建立', desc: '設計關鍵績效指標與目標值，確保可衡量' },
                { num: '04', title: '系統導入', desc: '建置績效管理系統，整合資料來源' },
                { num: '05', title: '持續優化', desc: '定期檢討與調整，確保策略有效執行' }
              ].map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="relative z-10 w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <span className="text-2xl font-bold text-white">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section id="cases" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-2">
                <span className="text-gray-400 font-light">客戶</span>
                <span className="text-[#1A3A52] italic">成功案例</span>
              </h2>
            </div>
            <div className="flex items-center gap-3 mt-6 md:mt-0">
              {industries.map((industry) => (
                <button
                  key={industry}
                  onClick={() => setSelectedIndustry(industry)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedIndustry === industry
                      ? 'bg-[#D4AF37] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredCases.map((caseItem, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-shadow overflow-hidden">
                <div className="w-full h-56">
                  <img src={caseItem.image} alt={caseItem.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-xs font-semibold mb-4">
                    {caseItem.industry}
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2 leading-tight">{caseItem.title}</h3>
                  <p className="text-sm text-gray-500 mb-6">{caseItem.company}</p>
                  <div className="space-y-3">
                    {caseItem.results.map((result, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{result.metric}</span>
                        <span className="text-2xl font-bold text-[#D4AF37]">{result.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="inline-block bg-[#D4AF37] text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
              CLIENT REVIEWS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52]">客戶怎麼說</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14">
                    <img src={review.avatar} alt={review.name} className="w-full h-full rounded-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="font-bold text-[#1A3A52] text-base">{review.name}</div>
                    <div className="text-xs text-gray-500">{review.company}</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  "{review.content}"
                </p>
                <div className="flex items-center gap-1">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#D4AF37] text-base"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultant Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#D4AF37] text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
              EXPERT TEAM
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-4">顧問團隊</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">結合財務、顧客、流程、成長四大專業領域，為您提供全方位策略諮詢</p>
          </div>

          {/* 服務業BSC顧問 */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1A3A52] rounded-xl flex items-center justify-center">
                  <i className="ri-store-2-line text-white text-lg"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A3A52]">服務業 BSC 顧問</h3>
              </div>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  name: '柯昆宏',
                  title: '會計師',
                  perspective: '財務構面',
                  color: '#1A3A52',
                  icon: 'ri-money-dollar-circle-line',
                  avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/43793d55b273ac413da77df4b40303d1.jpeg',
                  expertise: ['製造成本控制', '資本支出規劃', '產線投資報酬', '財務績效優化']
                },
                {
                  name: '林雅婷',
                  title: '供應鏈顧問',
                  perspective: '顧客構面',
                  color: '#D4AF37',
                  icon: 'ri-user-heart-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20supply%20chain%20consultant%20in%20smart%20business%20attire%2C%20confident%20corporate%20portrait%20with%20clean%20light%20background%2C%20experienced%20logistics%20and%20operations%20expert%20with%20warm%20professional%20expression%2C%20polished%20appearance&width=200&height=200&seq=mfg_consultant2&orientation=squarish',
                  expertise: ['客戶交期管理', '品質滿意度', '供應商關係', '市場需求預測']
                },
                {
                  name: '吳志明',
                  title: '生產管理顧問',
                  perspective: '內部流程',
                  color: '#50C878',
                  icon: 'ri-settings-3-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20production%20management%20consultant%20in%20business%20casual%20attire%2C%20confident%20industrial%20expert%20portrait%20with%20clean%20background%2C%20experienced%20manufacturing%20operations%20specialist%20with%20focused%20professional%20expression%2C%20smart%20appearance&width=200&height=200&seq=mfg_consultant3&orientation=squarish',
                  expertise: ['精實生產導入', '製程品質管理', '設備效率提升', '生產排程優化']
                },
                {
                  name: '蔡佳穎',
                  title: '人才培育顧問',
                  perspective: '學習成長',
                  color: '#9B59B6',
                  icon: 'ri-lightbulb-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20talent%20development%20consultant%20in%20elegant%20business%20attire%2C%20confident%20HR%20and%20training%20expert%20portrait%20with%20clean%20neutral%20background%2C%20experienced%20organizational%20development%20specialist%20with%20inspiring%20professional%20expression%2C%20refined%20appearance&width=200&height=200&seq=mfg_consultant4&orientation=squarish',
                  expertise: ['技術人才培育', '工廠文化轉型', '技能矩陣建立', '創新改善文化']
                }
              ].map((consultant, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col items-center p-8 text-center">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-xs font-semibold mb-5"
                    style={{ backgroundColor: consultant.color }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${consultant.icon} text-sm`}></i>
                    </div>
                    {consultant.perspective}
                  </div>
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden border-4 mb-4"
                    style={{ borderColor: consultant.color }}
                  >
                    <img
                      src={consultant.avatar}
                      alt={consultant.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-1">{consultant.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{consultant.title}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {consultant.expertise.map((skill, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 製造業BSC顧問 */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-xl flex items-center justify-center">
                  <i className="ri-building-2-line text-white text-lg"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A3A52]">製造業 BSC 顧問</h3>
              </div>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  name: '柯昆宏',
                  title: '會計師',
                  perspective: '財務構面',
                  color: '#1A3A52',
                  icon: 'ri-money-dollar-circle-line',
                  avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/43793d55b273ac413da77df4b40303d1.jpeg',
                  expertise: ['製造成本控制', '資本支出規劃', '產線投資報酬', '財務績效優化']
                },
                {
                  name: '林雅婷',
                  title: '供應鏈顧問',
                  perspective: '顧客構面',
                  color: '#D4AF37',
                  icon: 'ri-user-heart-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20supply%20chain%20consultant%20in%20smart%20business%20attire%2C%20confident%20corporate%20portrait%20with%20clean%20light%20background%2C%20experienced%20logistics%20and%20operations%20expert%20with%20warm%20professional%20expression%2C%20polished%20appearance&width=200&height=200&seq=mfg_consultant2&orientation=squarish',
                  expertise: ['客戶交期管理', '品質滿意度', '供應商關係', '市場需求預測']
                },
                {
                  name: '吳志明',
                  title: '生產管理顧問',
                  perspective: '內部流程',
                  color: '#50C878',
                  icon: 'ri-settings-3-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20production%20management%20consultant%20in%20business%20casual%20attire%2C%20confident%20industrial%20expert%20portrait%20with%20clean%20background%2C%20experienced%20manufacturing%20operations%20specialist%20with%20focused%20professional%20expression%2C%20smart%20appearance&width=200&height=200&seq=mfg_consultant3&orientation=squarish',
                  expertise: ['精實生產導入', '製程品質管理', '設備效率提升', '生產排程優化']
                },
                {
                  name: '蔡佳穎',
                  title: '人才培育顧問',
                  perspective: '學習成長',
                  color: '#9B59B6',
                  icon: 'ri-lightbulb-line',
                  avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20talent%20development%20consultant%20in%20elegant%20business%20attire%2C%20confident%20HR%20and%20training%20expert%20portrait%20with%20clean%20neutral%20background%2C%20experienced%20organizational%20development%20specialist%20with%20inspiring%20professional%20expression%2C%20refined%20appearance&width=200&height=200&seq=mfg_consultant4&orientation=squarish',
                  expertise: ['技術人才培育', '工廠文化轉型', '技能矩陣建立', '創新改善文化']
                }
              ].map((consultant, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col items-center p-8 text-center">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-xs font-semibold mb-5"
                    style={{ backgroundColor: consultant.color }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${consultant.icon} text-sm`}></i>
                    </div>
                    {consultant.perspective}
                  </div>
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden border-4 mb-4"
                    style={{ borderColor: consultant.color }}
                  >
                    <img
                      src={consultant.avatar}
                      alt={consultant.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-1">{consultant.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{consultant.title}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {consultant.expertise.map((skill, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/team" className="inline-block border-2 border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#D4AF37] hover:text-white transition-all whitespace-nowrap cursor-pointer">
              了解更多顧問資訊
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#D4AF37] text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
              FAQ
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-4">常見問題</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">關於平衡計分卡導入，您可能想了解的問題</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: '什麼是平衡計分卡（BSC）？',
                answer: '平衡計分卡（Balanced Scorecard, BSC）是一套策略管理工具，由哈佛商學院教授 Kaplan 與 Norton 於1992年提出。它從財務、顧客、內部流程、學習與成長四個構面，全面衡量企業績效。'
              },
              {
                question: '導入平衡計分卡需要多長時間？',
                answer: '一般而言，完整的平衡計分卡導入專案約需3至6個月。包含需求訪談、策略共識建立、指標設計、系統導入與初步運行。實際時間會依企業規模、組織複雜度與配合程度而有所調整。'
              },
              {
                question: '平衡計分卡適合哪些產業？',
                answer: '平衡計分卡適用於所有產業，包括製造業、服務業、科技業、金融業、醫療業、教育機構及政府單位等。全球超過50%的財富500大企業採用BSC作為策略管理工具。'
              },
              {
                question: '中小企業也適合導入平衡計分卡嗎？',
                answer: '非常適合。中小企業資源有限，更需要聚焦策略方向。平衡計分卡能幫助中小企業釐清優先順序，將有限資源投入最關鍵的策略目標，避免資源分散。'
              },
              {
                question: '導入平衡計分卡後，多久能看到成效？',
                answer: '通常在導入後3至6個月即可看到初步成效，例如策略共識提升、目標更加明確、跨部門協作改善等。'
              },
              {
                question: '平衡計分卡與KPI有什麼不同？',
                answer: 'KPI（關鍵績效指標）是衡量績效的工具，而平衡計分卡是一套完整的策略管理框架。BSC不僅包含KPI，還涵蓋策略地圖、目標值設定、行動方案規劃等。'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between px-8 py-6 text-left cursor-pointer group"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-[#D4AF37] font-bold text-sm min-w-[28px]">Q{index + 1}</span>
                    <span className="text-[#1A3A52] font-semibold text-base group-hover:text-[#D4AF37] transition-colors">{faq.question}</span>
                  </div>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${openFaqIndex === index ? 'bg-[#D4AF37] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-lg ${openFaqIndex === index ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-8 pb-6 pl-20">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm mb-4">還有其他問題嗎？</p>
            <a href="#contact" className="inline-block bg-[#D4AF37] text-white px-8 py-3 rounded-full text-lg font-bold hover:bg-[#C19B2B] transition-all whitespace-nowrap cursor-pointer">
              立即諮詢我們
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1A3A52] via-[#2C5F7F] to-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <div className="w-full h-64 rounded-3xl overflow-hidden mb-8">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20business%20handshake%20in%20modern%20office%20setting%2C%20successful%20partnership%20and%20collaboration%20moment%2C%20bright%20natural%20lighting%20with%20clean%20corporate%20environment%2C%20emphasizing%20trust%20and%20strategic%20cooperation&width=1200&height=400&seq=cta1&orientation=landscape" 
                alt="Partnership" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            <span className="font-light">準備好</span><br />
            <span className="font-bold text-[#D4AF37]">提升企業績效</span><br />
            <span className="italic">了嗎？</span>
          </h2>
          <p className="text-gray-200 text-lg mb-8">讓我們協助您將策略轉化為實際成果</p>
          <a href="#contact" className="inline-block bg-[#D4AF37] text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-[#C19B2B] hover:text-white transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
            立即預約免費諮詢
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-4">聯絡我們</h2>
            <p className="text-gray-600 text-lg">填寫表單，我們將盡快與您聯繫</p>
          </div>

          <form 
            id="contact_form"
            data-readdy-form
            action="https://readdy.ai/api/form/d68l51ua728k8ctu2bcg"
            method="POST"
            className="bg-[#F5F7FA] rounded-3xl p-8 md:p-12"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validation
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const phone = formData.get('phone') as string;
              const company = formData.get('company') as string;
              const message = formData.get('message') as string;

              if (!name || !email || !phone || !company || !message) {
                alert('請填寫所有必填欄位');
                return;
              }

              if (message.length > 500) {
                alert('訊息內容不得超過500字');
                return;
              }

              // Convert to URL encoded format
              const urlEncodedData = new URLSearchParams();
              formData.forEach((value, key) => {
                urlEncodedData.append(key, value.toString());
              });

              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: urlEncodedData.toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('感謝您的聯繫！我們會盡快回覆您。');
                  form.reset();
                } else {
                  alert('提交失敗，請稍後再試。');
                }
              })
              .catch(() => {
                alert('提交失敗，請稍後再試。');
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-[#1A3A52] mb-2">姓名 *</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all text-sm"
                  placeholder="請輸入您的姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A3A52] mb-2">電子郵件 *</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all text-sm"
                  placeholder="example@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-[#1A3A52] mb-2">聯絡電話 *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all text-sm"
                  placeholder="0912-345-678"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A3A52] mb-2">公司名稱 *</label>
                <input
                  type="text"
                  name="company"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all text-sm"
                  placeholder="請輸入公司名稱"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#1A3A52] mb-2">需求說明 * (限500字)</label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all resize-none text-sm"
                placeholder="請簡述您的需求與期望..."
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-[#D4AF37] text-white py-4 rounded-xl font-bold text-base hover:bg-[#C19B2B] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              送出諮詢需求
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3A52] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <img src="https://public.readdy.ai/ai/img_res/9fa41e12-cf6f-466e-a521-df80e2d2b6a9.png" alt="Logo" className="h-12 mb-4 brightness-0 invert" />
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                專業的平衡計分卡顧問團隊，協助企業將策略轉化為可衡量的行動方案，實現永續成長與卓越績效。
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">快速連結</h3>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">關於我們</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">服務項目</a></li>
                <li><Link to="/team" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">顧問團隊</Link></li>
                <li><a href="#cases" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">成功案例</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">部落格文章</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">聯絡我們</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">我們的服務</h3>
              <ul className="space-y-3">
                <li><span className="text-gray-400 text-sm">平衡計分卡導入</span></li>
                <li><span className="text-gray-400 text-sm">策略規劃諮詢</span></li>
                <li><span className="text-gray-400 text-sm">KPI指標設計</span></li>
                <li><span className="text-gray-400 text-sm">教育訓練課程</span></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">訂閱電子報</h3>
              <p className="text-gray-400 text-sm mb-4">獲取最新管理知識</p>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="您的電子郵件"
                  className="flex-1 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-[#D4AF37] text-sm"
                />
                <button className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center hover:bg-[#C19B2B] transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-xs">© 2025 平衡計分卡顧問公司. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D4AF37] text-xs transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
